import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as responsiblePersons from '../../responsible-persons'
import type { TypeParams } from '../types'

/**
 * After a new employee is created, automatically adds them to the current
 * responsible-person selection so that they appear checked in the employee
 * selector without any further user interaction.
 */
export const autoSelectOnCreateForResponsiblePersons = store.effects.create(
  (
    _: ec.Collection<TypeParams>['feature'],
    collection: ec.Collection<TypeParams>,
    recordResponsiblePersonsEc: responsiblePersons.recordResponsiblePersons.Collection,
    clusterResponsiblePersonsEc: responsiblePersons.clusterResponsiblePersons.Collection,
  ) =>
    collection.plugins.dataOperationPlugin.feature.actions.create.success
      .observe()
      .pipe(
        rxjs.mergeMap(action =>
          rxjs
            .combineLatest([
              recordResponsiblePersonsEc.plugins.currentEntityPlugin.feature.selectors.isCreating()
                .asObservable,
              clusterResponsiblePersonsEc.plugins.currentEntityPlugin.feature.selectors.isCreating()
                .asObservable,
            ])
            .pipe(
              rxjs.take(1),
              rxjs.mergeMap(([isCreatingRecord, isCreatingCluster]) => {
                const employees = action.data.response ?? []
                if (!employees.length) {
                  return rxjs.EMPTY
                }

                const emits: rxjs.Observable<any>[] = []

                if (isCreatingRecord) {
                  emits.push(
                    rxjs.of(
                      collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
                        .from(
                          'autoSelectOnCreateForResponsiblePersons (effect)',
                        )
                        .create({
                          key: recordResponsiblePersonsEc.associations
                            ?.selectionTarget,
                          value: {
                            add: employees.map(e => collection.identify(e)),
                          },
                        }),
                    ),
                  )
                }

                if (isCreatingCluster) {
                  emits.push(
                    rxjs.of(
                      collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
                        .from(
                          'autoSelectOnCreateForResponsiblePersons (effect)',
                        )
                        .create({
                          key: clusterResponsiblePersonsEc.associations
                            ?.selectionTarget,
                          value: {
                            add: employees.map(e => collection.identify(e)),
                          },
                        }),
                    ),
                  )
                }

                return rxjs.merge(...emits)
              }),
            ),
        ),
      ),
)
